import React, { Dispatch, useEffect, useRef, lazy, Suspense } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { selectConversation } from '@repo/redux/reducers/data/nexus';
import { ConversationMessage } from '@repo/utils/models/nexus';
import { ID } from '@repo/utils/types';
import { Pill } from '@repo/components/containers';
import styles from './Conversation.module.scss';
import {
  ActionCopyIcon,
  ChevronRightIcon,
  RegenerateIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  XIcon,
} from '@repo/icons';
import { fetchContact, selectContact } from '@repo/redux/actions/contacts';
import { alertUser } from '@repo/redux/actions/alerts';
import ConversationStarterSuggestions from '../ConversationStarterSuggestions/ConversationStarterSuggestions';
import { clearNewConversation, regenerateLastResponse } from '@repo/redux/actions/nexus';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContactCard } from '../ConversationStarterSuggestions/ContactCard/ContactCard';
import { Overlay, Tooltip } from '@repo/components/tooltips';
import Button from '@components/Button';
import { Link } from 'react-router-dom';

const ReactMarkdown = lazy(() => import('react-markdown'));

const UserMessage = ({ text }: { text: string }) => (
  <div className={cn(styles.message, styles.userMessage)}>{text}</div>
);

const TooltippedIcon = ({ children, tooltipText, onClick }) => (
  <Tooltip overlay={<Overlay>{tooltipText}</Overlay>} placement="bottom" delay>
    <Button onClick={onClick}>{children}</Button>
  </Tooltip>
);

const RelevantContactPill = ({ contactID }: { contactID: ID }) => {
  const dispatch = useDispatch();
  const contact = useSelector(selectContact(contactID));
  const { isFetching, isPersisted } = contact;

  useEffect(() => {
    if (!isPersisted) {
      dispatch(fetchContact({ id: contactID }));
    }
  }, []);

  return !isFetching && <Pill contact={contact} clickable />;
};

const RelevantContacts = ({ contactIDs }: { contactIDs: ID[] }) => {
  return (
    <div className={styles.relevantContacts}>
      {contactIDs.map((contactID) => (
        <RelevantContactPill key={contactID} contactID={contactID} />
      ))}
    </div>
  );
};

const renderBotLink = ({ children, ...props }) => {
  const { href } = props.node?.properties || {};

  if (href?.includes('web.clay.earth')) {
    const location = href.split('web.clay.earth').pop();
    return (
      <Link to={location} onClick={(e) => e.stopPropagation()}>
        {children}
      </Link>
    );
  }

  return <a {...props}>{children}</a>;
};

const renderBotImage = ({ ...props }) => {
  const { src, alt } = props.node?.properties || {};

  if (src?.includes('web.clay.earth')) {
    const location = src.split('web.clay.earth').pop();
    return (
      <Link to={location} onClick={(e) => e.stopPropagation()}>
        {alt}
      </Link>
    );
  }

  return <img {...props} />;
};

const BotMessage = ({
  text,
  relevantContacts,
  terminated,
  error,
  paywall,
  regenerate,
}: Partial<ConversationMessage> & { paywall?: boolean; regenerate?: () => void }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const submitFeedback = () => dispatch(alertUser('Thanks for the feedback!', 'success'));
  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => dispatch(alertUser('Copied to clipboard', 'success')));
  };

  return (
    <>
      <div
        className={cn(styles.message, styles.botMessage, (error || paywall) && styles.messageError)}
      >
        {error ? (
          <p>
            {text}{' '}
            <span
              className={cn(styles.startOverBtn)}
              onClick={() => {
                dispatch(clearNewConversation);
                if (location.pathname !== '/nexus') {
                  navigate('/nexus');
                }
              }}
            >
              {' '}
              start a new conversation
              <ChevronRightIcon width="11" height="11" color="textSecondary" />
            </span>
          </p>
        ) : (
          <Suspense fallback={<div />}>
            <ReactMarkdown
              key={terminated ? 'terminated' : 'streaming'}
              children={text}
              components={{
                a: renderBotLink,
                img: renderBotImage,
              }}
              linkTarget="_blank"
            />
          </Suspense>
        )}
        {!error && relevantContacts && relevantContacts.length > 0 && (
          <div className={cn(styles.messageContactsList, terminated && styles.messageTerminated)}>
            <RelevantContacts contactIDs={relevantContacts} />
          </div>
        )}
      </div>
      {!error && !paywall && (
        <div className={cn(styles.messageUtilityBar, terminated && styles.messageTerminated)}>
          <TooltippedIcon tooltipText="Copy response" onClick={copyToClipboard}>
            <ActionCopyIcon
              width="16"
              height="16"
              color="textSecondary"
              hoverOpacity={false}
              hover
            />
          </TooltippedIcon>
          <TooltippedIcon tooltipText="Good response" onClick={submitFeedback}>
            <ThumbsUpIcon />
          </TooltippedIcon>
          <TooltippedIcon tooltipText="Bad response" onClick={submitFeedback}>
            <ThumbsDownIcon />
          </TooltippedIcon>
          {!!regenerate && (
            <TooltippedIcon tooltipText="Regenerate" onClick={regenerate}>
              <RegenerateIcon color="textSecondary" hover />
            </TooltippedIcon>
          )}
        </div>
      )}
    </>
  );
};

export default function ChatConversation({
  conversationID = 'new',
  setConversationTargetContactID,
}: {
  conversationID: ID;
  setConversationTargetContactID?: Dispatch<ID>;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const conversation = useSelector(selectConversation(conversationID));
  const conversationTargetContact = useSelector(selectContact(conversation.targetContactID));
  const messages = conversation.messages
    .valueSeq()
    .toArray()
    .filter((message) => !!message.text);

  const renderMessage = (message: ConversationMessage) => {
    let regenerate = undefined;
    if (message.id === conversation.messages.last()?.id) {
      regenerate = () =>
        dispatch(regenerateLastResponse(conversationID, conversation.targetContactID));
    }

    if (!message.text) {
      return null;
    }

    if (message.fromUser) {
      return <UserMessage key={message.id} text={message.text} />;
    } else {
      return (
        <BotMessage
          key={message.id}
          text={message.text}
          relevantContacts={conversation.includeTargetContactID(message.relevantContacts)}
          terminated={message.terminated}
          error={message.error}
          regenerate={regenerate}
        />
      );
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <>
      {messages.length === 0 ? (
        <ConversationStarterSuggestions setTargetContactID={setConversationTargetContactID} />
      ) : (
        <div className={styles.conversation} ref={containerRef}>
          {conversation.targetContactID && (
            <div className={styles.conversationContactCard}>
              <ContactCard contact={conversationTargetContact} detailType="lastInteraction" />
              <XIcon
                color="textSecondary"
                className={styles.closeIcon}
                onClick={() => {
                  dispatch(clearNewConversation);
                  setConversationTargetContactID(null);
                  if (location.pathname !== '/nexus') {
                    navigate('/nexus');
                  }
                }}
                width={13}
                height={13}
                hover
              />
            </div>
          )}

          {messages.map(renderMessage)}

          {conversation.nexusDisabled && (
            <BotMessage
              text="Hi! You need to enable Nexus to get started. Please do so using the button below."
              paywall
            />
          )}
        </div>
      )}
    </>
  );
}
